/**
 * Sidebar navigation preferences — ONE contract shared by TekStak and the
 * GAS dashboard so "hide a page" and "reorder the rail" mean exactly the
 * same thing in both shells.
 *
 * The two apps store the blob in different places (TekStak:
 * `user_preferences.nav_layout`; GAS: the scoped `NAV_LAYOUT`
 * UserProperty) — what is shared is the SHAPE and the pure functions that
 * apply it, so a preference written by one shell reads identically in the
 * other.
 *
 * INVARIANT — preferences may only SUBTRACT.
 * `applyNavPrefs` never adds an item that wasn't handed to it. Role and
 * feature gates run FIRST and stay authoritative; a user hiding a page is
 * a UX choice, never an access-control one, and un-hiding a page they were
 * never granted is impossible by construction.
 *
 * INVARIANT — unknown keys survive a round-trip.
 * A page gated off today (admin-only, feature-flagged, in-tenant-only)
 * still owns its slot in `order` and its entry in `hidden`. Pruning to the
 * currently-visible set would silently reset a teammate's layout every
 * time a flag flipped.
 *
 * New pages are VISIBLE by default and append at the end of the rail: a
 * shipped page nobody can find is worse than a rail that grew by one.
 */

export const NAV_PREFS_VERSION = 1;

export interface NavPrefs {
  /** Schema version — bump only on a breaking shape change. */
  v: number;
  /** Full display order, most-preferred first. Keys not listed append after. */
  order: string[];
  /** Keys the user switched off. */
  hidden: string[];
}

/** The "never customized" state — everything visible, natural order. */
export const EMPTY_NAV_PREFS: NavPrefs = Object.freeze({
  v: NAV_PREFS_VERSION,
  order: [],
  hidden: [],
}) as NavPrefs;

/**
 * Key for a nav item nested inside a group.
 *
 * Child ids are NOT globally unique in TekStak's rail (`stack-map` is both
 * a group and its own first child), so children get a namespaced key
 * rather than sharing the flat top-level space.
 */
export function navChildKey(parentKey: string, childKey: string): string {
  return `${parentKey}::${childKey}`;
}

function uniqueStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const key = entry.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * Coerce anything the wire, a UserProperty, or an old client hands us into
 * a valid NavPrefs. Never throws — a corrupt blob degrades to "no
 * customization", which is always a usable rail.
 */
export function normalizeNavPrefs(raw: unknown): NavPrefs {
  let value: unknown = raw;

  // GAS stores the blob as a JSON string in UserProperties.
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return { ...EMPTY_NAV_PREFS };
    try {
      value = JSON.parse(text);
    } catch {
      return { ...EMPTY_NAV_PREFS };
    }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...EMPTY_NAV_PREFS };
  }

  const obj = value as Record<string, unknown>;
  const version = typeof obj.v === 'number' && obj.v > 0 ? obj.v : NAV_PREFS_VERSION;

  // A blob from a FUTURE version is not ours to interpret — fall back
  // rather than half-apply a shape we don't understand.
  if (version > NAV_PREFS_VERSION) return { ...EMPTY_NAV_PREFS };

  return {
    v: NAV_PREFS_VERSION,
    order: uniqueStrings(obj.order),
    hidden: uniqueStrings(obj.hidden),
  };
}

/** True when the user switched this key off. */
export function isNavKeyHidden(prefs: NavPrefs | null | undefined, key: string): boolean {
  if (!prefs || !Array.isArray(prefs.hidden)) return false;
  return prefs.hidden.indexOf(key) !== -1;
}

/**
 * Sort a gated item list into the user's order. Items with no saved slot
 * keep their natural relative order and append after the ordered ones.
 *
 * Does NOT filter — see `applyNavPrefs`. Kept separate so the settings
 * panel can list hidden items in their real slots.
 */
export function sortByNavPrefs<T>(
  items: readonly T[],
  prefs: NavPrefs | null | undefined,
  getKey: (item: T) => string,
): T[] {
  const order = prefs && Array.isArray(prefs.order) ? prefs.order : [];
  if (!order.length) return items.slice();

  const rank = new Map<string, number>();
  order.forEach((key, index) => rank.set(key, index));

  // Decorate-sort-undecorate: the natural index keeps unranked items in
  // source order without leaning on sort stability. Unranked items rank
  // AFTER every ranked one — `items.length + order.length` is past any
  // real rank, and subtracting two finite sentinels stays a valid
  // comparator (Infinity - Infinity would be NaN).
  const unranked = order.length + items.length + 1;

  return items
    .map((item, index) => {
      const key = getKey(item);
      return { item, index, rank: rank.has(key) ? (rank.get(key) as number) : unranked };
    })
    .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
    .map((entry) => entry.item);
}

/**
 * The one function every shell's sidebar calls: order the gated items and
 * drop the ones the user switched off.
 *
 * `items` MUST already be role/feature filtered — this only subtracts.
 */
export function applyNavPrefs<T>(
  items: readonly T[],
  prefs: NavPrefs | null | undefined,
  getKey: (item: T) => string,
): T[] {
  return sortByNavPrefs(items, prefs, getKey).filter((item) => !isNavKeyHidden(prefs, getKey(item)));
}

export interface NavPrefsEntry<T> {
  item: T;
  key: string;
  visible: boolean;
}

/**
 * Every gated item in the user's order, each tagged with its on/off state.
 * This is what the settings panel renders — hidden pages must stay listed
 * or there is no way to switch them back on.
 */
export function navPrefsEntries<T>(
  items: readonly T[],
  prefs: NavPrefs | null | undefined,
  getKey: (item: T) => string,
): Array<NavPrefsEntry<T>> {
  return sortByNavPrefs(items, prefs, getKey).map((item) => {
    const key = getKey(item);
    return { item, key, visible: !isNavKeyHidden(prefs, key) };
  });
}

/** Switch one page on or off. Returns a new NavPrefs; never mutates. */
export function setNavKeyVisible(
  prefs: NavPrefs | null | undefined,
  key: string,
  visible: boolean,
): NavPrefs {
  const base = normalizeNavPrefs(prefs);
  const hidden = base.hidden.filter((entry) => entry !== key);
  if (!visible) hidden.push(key);
  return { v: NAV_PREFS_VERSION, order: base.order, hidden };
}

/**
 * Commit a new display order for ONE list (the top-level rail, or one
 * group's children).
 *
 * `keys` is that list's full order after the drag. Keys from other lists —
 * and from pages currently gated off — are preserved after it, so
 * reordering the Tools group never disturbs the top-level rail and never
 * discards a flagged-off page's slot.
 */
export function setNavOrder(
  prefs: NavPrefs | null | undefined,
  keys: readonly string[],
): NavPrefs {
  const base = normalizeNavPrefs(prefs);
  const next = uniqueStrings(keys as string[]);
  const nextSet = new Set(next);
  for (const key of base.order) {
    if (!nextSet.has(key)) next.push(key);
  }
  return { v: NAV_PREFS_VERSION, order: next, hidden: base.hidden };
}

/**
 * Move one key within a list and commit the result. `keys` is the list's
 * current display order; indices are positions inside it.
 */
export function moveNavKey(
  prefs: NavPrefs | null | undefined,
  keys: readonly string[],
  fromIndex: number,
  toIndex: number,
): NavPrefs {
  const list = uniqueStrings(keys as string[]);
  if (
    fromIndex < 0 || fromIndex >= list.length ||
    toIndex < 0 || toIndex >= list.length ||
    fromIndex === toIndex
  ) {
    return normalizeNavPrefs(prefs);
  }
  const next = list.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return setNavOrder(prefs, next);
}

/** True when the user has customized anything at all. */
export function hasNavCustomization(prefs: NavPrefs | null | undefined): boolean {
  const base = normalizeNavPrefs(prefs);
  return base.order.length > 0 || base.hidden.length > 0;
}

/** Back to the shipped rail. */
export function resetNavPrefs(): NavPrefs {
  return { v: NAV_PREFS_VERSION, order: [], hidden: [] };
}
